// src/pages/admin/AdminInformativos.js

import React, { useEffect, useState } from 'react';
import { getInformativos, createInformativo, updateInformativo, deleteInformativo } from '../../services/api';
import Toast from '../../components/common/Toast';

const TIPOS = [
  { value: 'aviso',    label: 'Aviso',    icon: '📌', cor: '#c9a030' },
  { value: 'evento',   label: 'Evento',   icon: '🗓', cor: '#1a3a6b' },
  { value: 'pastoral', label: 'Pastoral', icon: '✝️', cor: '#1e7d4f' },
  { value: 'urgente',  label: 'Urgente',  icon: '🔔', cor: '#c0392b' },
];

const TIPO_CONFIG = {
  aviso:    { cor: '#c9a030', bg: '#f5e9c8' },
  evento:   { cor: '#1a3a6b', bg: '#e8eef7' },
  pastoral: { cor: '#1e7d4f', bg: '#eafaf1' },
  urgente:  { cor: '#c0392b', bg: '#fdedec' },
};

const initialForm = { titulo: '', texto: '', tipo: 'aviso' };

const AdminInformativos = () => {
  const [informativos, setInformativos] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    getInformativos().then(r => setInformativos(r?.data || [])).catch(() => {});
  };
  useEffect(load, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim() || !form.texto.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await updateInformativo({ ...form, id: editId });
        setToast({ msg: 'Informativo atualizado!', type: 'success' });
      } else {
        await createInformativo(form);
        setToast({ msg: 'Informativo criado!', type: 'success' });
      }
      setForm(initialForm);
      setEditId(null);
      setShowForm(false);
      load();
    } catch {
      setToast({ msg: 'Erro ao salvar.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este informativo?')) return;
    try {
      await deleteInformativo(id);
      setToast({ msg: 'Informativo excluído.', type: 'success' });
      load();
    } catch {
      setToast({ msg: 'Erro ao excluir.', type: 'error' });
    }
  };

  const handleEdit = (inf) => {
    setForm({ titulo: inf.titulo || '', texto: inf.texto || '', tipo: inf.tipo || 'aviso' });
    setEditId(inf.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelar = () => {
    setShowForm(false);
    setEditId(null);
    setForm(initialForm);
  };

  return (
    <div style={{ padding: 'var(--space-2xl)' }}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-xl)', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ marginBottom: '0.25rem' }}>Mural de Avisos</h2>
          <p style={{ color: 'var(--gray-400)', fontSize: '0.9rem' }}>
            {informativos.length} informativo{informativos.length !== 1 ? 's' : ''} publicado{informativos.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { cancelar(); setShowForm(true); }}>
          + Novo Informativo
        </button>
      </div>

      {/* Formulário */}
      {showForm && (
        <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-md)', marginBottom: 'var(--space-xl)', border: '2px solid var(--gold-pale)' }}>
          <h3 style={{ marginBottom: 'var(--space-lg)', fontSize: '1.1rem' }}>
            {editId ? '✏️ Editar Informativo' : '📋 Novo Informativo'}
          </h3>
          <form onSubmit={handleSave} noValidate>
            {/* Tipo */}
            <div className="form-group">
              <label className="form-label">Tipo <span className="required">*</span></label>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {TIPOS.map(({ value, label, icon, cor }) => (
                  <label key={value} style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)',
                    border: `2px solid ${form.tipo === value ? cor : 'var(--gray-200)'}`,
                    background: form.tipo === value ? TIPO_CONFIG[value].bg : 'var(--white)',
                    cursor: 'pointer', transition: 'all 0.15s', fontSize: '0.875rem',
                    fontWeight: form.tipo === value ? 700 : 400,
                  }}>
                    <input type="radio" name="tipo" value={value}
                      checked={form.tipo === value}
                      onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                      style={{ display: 'none' }} />
                    <span>{icon}</span> {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Título <span className="required">*</span></label>
              <input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                className="form-input" placeholder="Ex: Missa de Aniversário da Paróquia" required />
            </div>

            <div className="form-group">
              <label className="form-label">Texto <span className="required">*</span></label>
              <textarea value={form.texto} onChange={e => setForm(f => ({ ...f, texto: e.target.value }))}
                className="form-textarea" rows={4}
                placeholder="Descreva o informativo com detalhes..." required />
            </div>

            {/* Preview */}
            {form.titulo && form.texto && (
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <p style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: '0.5rem' }}>Preview:</p>
                <div style={{ maxWidth: 280, borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-md)', transform: 'rotate(0.5deg)' }}>
                  <div style={{ background: TIPO_CONFIG[form.tipo].cor, padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{TIPOS.find(t => t.value === form.tipo)?.icon}</span>
                    <span style={{ color: 'white', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      {TIPOS.find(t => t.value === form.tipo)?.label}
                    </span>
                    <div style={{ marginLeft: 'auto', width: 10, height: 10, borderRadius: '50%', background: 'rgba(255,255,255,0.4)', border: '2px solid rgba(255,255,255,0.7)' }} />
                  </div>
                  <div style={{ padding: '1rem 1.25rem 1.25rem', background: TIPO_CONFIG[form.tipo].bg }}>
                    <h4 style={{ color: '#1a2744', fontSize: '0.9rem', marginBottom: '0.4rem', fontFamily: 'var(--font-display)' }}>{form.titulo}</h4>
                    <p style={{ color: '#6b6860', fontSize: '0.82rem', lineHeight: 1.6 }}>{form.texto}</p>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-sm"
                style={{ background: 'var(--gray-100)', color: 'var(--gray-600)' }}
                onClick={cancelar}>Cancelar</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                {saving ? 'Salvando...' : editId ? 'Salvar alterações' : 'Publicar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de informativos */}
      {informativos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--gray-400)' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
          <p>Nenhum informativo publicado ainda.</p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Clique em "Novo Informativo" para criar o primeiro aviso.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'var(--space-lg)' }}>
          {informativos.map((inf, i) => {
            const cfg = TIPO_CONFIG[inf.tipo] || TIPO_CONFIG.aviso;
            const tipo = TIPOS.find(t => t.value === inf.tipo) || TIPOS[0];
            return (
              <div key={i} style={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-md)', border: `1px solid ${cfg.cor}22` }}>
                <div style={{ background: cfg.cor, padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{tipo.icon}</span>
                  <span style={{ color: 'white', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{tipo.label}</span>
                  <div style={{ marginLeft: 'auto', width: 10, height: 10, borderRadius: '50%', background: 'rgba(255,255,255,0.4)', border: '2px solid rgba(255,255,255,0.7)' }} />
                </div>
                <div style={{ padding: '1rem 1.25rem', background: cfg.bg }}>
                  <h4 style={{ color: '#1a2744', fontSize: '0.95rem', marginBottom: '0.4rem', fontFamily: 'var(--font-display)' }}>{inf.titulo}</h4>
                  <p style={{ color: '#6b6860', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: '1rem' }}>{inf.texto}</p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-sm btn-navy" style={{ flex: 1 }} onClick={() => handleEdit(inf)}>✏️ Editar</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(inf.id)}>✕</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminInformativos;
